import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ShoppingCart from '../../components/ui/ShoppingCart';
import ContactHub from '../../components/ui/ContactHub';
import ServiceHero from './components/ServiceHero';
import ServiceSidebar from './components/ServiceSidebar';
import ServiceProcess from './components/ServiceProcess';
import BeforeAfterGallery from './components/BeforeAfterGallery';
import CustomerTestimonials from './components/CustomerTestimonials';
import ServiceFAQ from './components/ServiceFAQ';
import RelatedServices from './components/RelatedServices';
import BookingWidget from './components/BookingWidget';

const ServiceDetails = () => {
  const location = useLocation();
  const params = useParams();
  const [selectedService, setSelectedService] = useState(null);

  // Mock service data - now with all service types
  const services = {
    'house-cleaning': {
      id: 'house-cleaning',
      title: 'Professional House Cleaning',
      category: 'Residential Cleaning',
      icon: 'Home',
      description: `Keep your home spotless with our regular house cleaning service. Our professional team handles all the cleaning tasks so you can focus on what matters most to you.`,
      startingPrice: 89,
      duration: '2-3 hours',
      teamSize: '2 professionals',
      serviceArea: '25 mile radius',
      guarantee: '30-day satisfaction',
      heroImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop',
      includes: [
        'Kitchen cleaning and sanitization',
        'Bathroom deep cleaning and disinfection',
        'Living areas dusting and vacuuming',
        'Floor mopping and maintenance',
        'Trash removal and fresh liner placement',
        'Light fixture and switch plate cleaning',
        'Eco-friendly cleaning products',
        'Satisfaction guarantee'
      ]
    },
    'office-cleaning': {
      id: 'office-cleaning',
      title: 'Commercial Office Cleaning',
      category: 'Commercial Cleaning',
      icon: 'Building',
      description: `Professional office cleaning services to maintain a clean, healthy, and productive work environment for your team and clients.`,
      startingPrice: 199,
      duration: '3-5 hours',
      teamSize: '2-4 professionals',
      serviceArea: '25 mile radius',
      guarantee: '30-day satisfaction',
      heroImage: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop',
      includes: [
        'Workstation and desk cleaning',
        'Conference room sanitization',
        'Kitchen and break room cleaning',
        'Restroom deep cleaning and restocking',
        'Floor vacuuming and mopping',
        'Trash removal and recycling',
        'Window and glass surface cleaning',
        'After-hours service availability'
      ]
    },
    'carpet-cleaning': {
      id: 'carpet-cleaning',
      title: 'Professional Carpet Cleaning',
      category: 'Specialized Cleaning',
      icon: 'Square',
      description: `Transform your carpets with our deep cleaning process that removes embedded dirt, stains, and allergens. Our certified technicians use eco-friendly solutions and state-of-the-art equipment to restore your carpets to like-new condition.`,
      startingPrice: 149,
      duration: '2-4 hours',
      teamSize: '2-3 professionals',
      serviceArea: '25 mile radius',
      guarantee: '30-day satisfaction',
      heroImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop',
      includes: [
        'Pre-treatment of stains and high-traffic areas',
        'Deep steam cleaning with truck-mounted equipment',
        'Eco-friendly cleaning solutions safe for pets and children',
        'Furniture moving (lightweight items)',
        'Post-cleaning inspection and touch-ups',
        'Carpet protection treatment application',
        'Quick-dry process (2-4 hours)',
        '30-day satisfaction guarantee'
      ]
    },
    'deep-cleaning': {
      id: 'deep-cleaning',
      title: 'Deep House Cleaning',
      category: 'Residential Cleaning',
      icon: 'Sparkles',
      description: `Our comprehensive deep cleaning service covers every corner of your home. Perfect for seasonal cleaning, move-in/move-out, or when you need that extra level of cleanliness.`,
      startingPrice: 299,
      duration: '4-8 hours',
      teamSize: '3-4 professionals',
      serviceArea: '25 mile radius',
      guarantee: '30-day satisfaction',
      heroImage: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&h=600&fit=crop',
      includes: [
        'Complete kitchen deep clean including appliances',
        'Bathroom sanitization and tile scrubbing',
        'Baseboards, window sills, and light fixtures',
        'Inside cabinets and drawers (empty)',
        'Ceiling fan cleaning',
        'Door frames and switch plates',
        'Detailed dusting of all surfaces',
        'Floor cleaning and mopping'
      ]
    },
    'post-construction': {
      id: 'post-construction',
      title: 'Post-Construction Cleanup',
      category: 'Specialized Cleaning',
      icon: 'HardHat',
      description: `Specialized cleaning service for construction sites and newly renovated spaces. We handle dust, debris, and construction residue with professional-grade equipment.`,
      startingPrice: 399,
      duration: '6-10 hours',
      teamSize: '3-5 professionals',
      serviceArea: '25 mile radius',
      guarantee: '30-day satisfaction',
      heroImage: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&h=600&fit=crop',
      includes: [
        'Construction dust and debris removal',
        'Window cleaning (interior and exterior)',
        'Floor deep cleaning and polishing',
        'Light fixture and ceiling fan cleaning',
        'HVAC vent cleaning and filter replacement',
        'Cabinet and appliance cleaning',
        'Paint splatter and adhesive removal',
        'Final inspection and touch-ups'
      ]
    },
    'disinfection': {
      id: 'disinfection',
      title: 'Disinfection Services',
      category: 'Specialized Cleaning',
      icon: 'Shield',
      description: `Professional disinfection services using hospital-grade sanitizers and EPA-approved products to eliminate viruses, bacteria, and harmful pathogens.`,
      startingPrice: 179,
      duration: '1-3 hours',
      teamSize: '2 professionals',
      serviceArea: '25 mile radius',
      guarantee: '30-day satisfaction',
      heroImage: 'https://images.unsplash.com/photo-1584464491033-06628f3a6b7b?w=800&h=600&fit=crop',
      includes: [
        'EPA-approved disinfectant application',
        'High-touch surface sanitization',
        'Electrostatic spraying technology',
        'Air purification treatment',
        'UV-C light sanitization (when applicable)',
        'Safe for families and pets',
        'Virus and bacteria elimination',
        'Health compliance certification'
      ]
    },
    'flooring': {
      id: 'flooring',
      title: 'Professional Floor Care',
      category: 'Specialized Cleaning',
      icon: 'Grid3x3',
      description: `Comprehensive floor cleaning and maintenance services for all floor types including hardwood, tile, laminate, and vinyl. Professional restoration and protection.`,
      startingPrice: 249,
      duration: '3-6 hours',
      teamSize: '2-3 professionals',
      serviceArea: '25 mile radius',
      guarantee: '30-day satisfaction',
      heroImage: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&h=600&fit=crop',
      includes: [
        'Hardwood floor deep cleaning and polishing',
        'Tile and grout restoration',
        'Laminate floor care and protection',
        'Stone floor sealing and maintenance',
        'Vinyl floor cleaning and restoration',
        'Floor waxing and protective coating',
        'Scratch and scuff removal',
        'Professional-grade equipment and products'
      ]
    }
  };

  const processSteps = [
    {
      icon: 'Phone',
      title: 'Book Your Service',
      description: 'Schedule your appointment online or call us. We provide instant quotes and flexible scheduling.',
      duration: '5 minutes'
    },
    {
      icon: 'Calendar',
      title: 'Confirmation & Prep',
      description: 'Receive confirmation with arrival time. Our team will contact you 30 minutes before arrival.',
      duration: '24 hours before'
    },
    {
      icon: 'Users',
      title: 'Professional Service',
      description: 'Our certified team arrives with all equipment and eco-friendly supplies to deliver exceptional results.',
      duration: '2-4 hours'
    },
    {
      icon: 'CheckCircle',
      title: 'Quality Inspection',
      description: 'Walk-through inspection with you to ensure complete satisfaction. Touch-ups included if needed.',
      duration: '15 minutes'
    }
  ];

  const beforeAfterProjects = [
    {
      title: 'Living Room Carpet Restoration',
      description: 'Heavy traffic carpet brought back to life with our deep cleaning process.',
      beforeImage: 'https://images.pexels.com/photos/4239091/pexels-photo-4239091.jpeg?w=800&h=600&fit=crop',
      afterImage: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop',
      location: 'Downtown Seattle',
      date: 'December 2024',
      duration: '3 hours'
    },
    {
      title: 'Bedroom Deep Clean Transformation',
      description: 'Complete bedroom makeover including carpet, furniture, and detailed cleaning.',
      beforeImage: 'https://images.pexels.com/photos/6195125/pexels-photo-6195125.jpeg?w=800&h=600&fit=crop',
      afterImage: 'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=800&h=600&fit=crop',
      location: 'Bellevue, WA',
      date: 'November 2024',
      duration: '4 hours'
    },
    {
      title: 'Office Space Carpet Cleaning',
      description: 'Commercial carpet cleaning for a busy office environment.',
      beforeImage: 'https://images.pexels.com/photos/4239013/pexels-photo-4239013.jpeg?w=800&h=600&fit=crop',
      afterImage: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop',
      location: 'Microsoft Campus',
      date: 'December 2024',
      duration: '6 hours'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      location: 'Seattle, WA',
      rating: 5,
      content: `Absolutely amazing service! My carpets look brand new after years of wear and tear. The team was professional, punctual, and went above and beyond. I'm so impressed with the results that I've already booked them for my upstairs carpets next month.`,
      avatar: 'https://randomuser.me/api/portraits/women/32.jpg',
      serviceType: 'Carpet Cleaning'
    },
    {
      name: 'Michael Chen',
      location: 'Bellevue, WA',
      rating: 5,
      content: `Clean360 exceeded all my expectations. They removed stains I thought were permanent and the whole house smells fresh and clean. The eco-friendly products they used were perfect for our family with young children. Highly recommended!`,
      avatar: 'https://randomuser.me/api/portraits/men/45.jpg',
      serviceType: 'Deep House Cleaning'
    },
    {
      name: 'Jennifer Martinez',
      location: 'Redmond, WA',
      rating: 5,
      content: `Professional, reliable, and thorough. The before and after difference was incredible. They took care of everything and left my home spotless. The pricing was fair and transparent with no hidden fees. Will definitely use again!`,
      avatar: 'https://randomuser.me/api/portraits/women/28.jpg',
      serviceType: 'House Cleaning'
    }
  ];

  const faqs = [
    {
      question: 'How long does the service take?',
      answer: 'Service duration varies by type and size. Most residential services take 2-4 hours, while deep cleaning and commercial services may take longer. We provide accurate time estimates during booking.',
      additionalInfo: 'We always provide estimated completion times during the booking process.'
    },
    {
      question: 'Are your cleaning products safe for pets and children?',
      answer: 'Yes, we use only eco-friendly, non-toxic cleaning solutions that are safe for your family and pets. All our products are certified green and biodegradable.',
      additionalInfo: 'We can provide ingredient lists for any products used upon request.'
    },
    {
      question: 'Do I need to be home during the service?',
      answer: 'While not required, we recommend being present for the initial walkthrough and final inspection. Many clients provide secure access and return for the completion walkthrough.',
      additionalInfo: 'We can arrange secure access procedures for your convenience.'
    },
    {
      question: 'What if I\'m not satisfied with the results?',
      answer: 'We offer a 30-day satisfaction guarantee. If you\'re not completely happy with our service, we\'ll return to address any concerns at no additional cost.',
      additionalInfo: 'Our goal is 100% customer satisfaction on every job.'
    },
    {
      question: 'How do I prepare for the service?',
      answer: 'Minimal preparation is needed. We recommend securing valuables, clearing clutter from surfaces, and providing access to areas to be cleaned. We handle the rest.',
      additionalInfo: 'We provide a detailed preparation checklist after booking.'
    }
  ];

  const relatedServices = [
    {
      title: 'Upholstery Cleaning',
      category: 'Furniture Care',
      description: 'Professional cleaning for sofas, chairs, and other upholstered furniture.',
      image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop',
      duration: '1-2 hours',
      startingPrice: 89,
      rating: 4.9,
      reviewCount: 342,
      isPopular: true
    },
    {
      title: 'Tile & Grout Cleaning',
      category: 'Floor Care',
      description: 'Deep cleaning and restoration of tile floors and grout lines.',
      image: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=400&h=300&fit=crop',
      duration: '2-3 hours',
      startingPrice: 129,
      rating: 4.8,
      reviewCount: 198,
      isPopular: false
    },
    {
      title: 'Window Cleaning',
      category: 'Exterior Care',
      description: 'Interior and exterior window cleaning for crystal clear views.',
      image: 'https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?w=400&h=300&fit=crop',
      duration: '1-3 hours',
      startingPrice: 79,
      rating: 4.7,
      reviewCount: 156,
      isPopular: false
    }
  ];

  useEffect(() => {
    // Get service type from URL params or query string
    let serviceType = params?.serviceType; // From /service-details/:serviceType route
    
    if (!serviceType) {
      // Fall back to query parameter for backward compatibility
      const urlParams = new URLSearchParams(location.search);
      serviceType = urlParams?.get('service');
    }
    
    // Default to first available service if no type specified
    if (!serviceType) {
      serviceType = Object.keys(services)?.[0];
    }
    
    setSelectedService(services?.[serviceType] || services?.[Object.keys(services)?.[0]]);
  }, [location, params]);

  const handleBookNow = () => {
    // Scroll to booking widget or open booking modal
    document.querySelector('.booking-widget')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleGetQuote = () => {
    console.log('Get quote clicked');
    // Implement quote request logic
  };

  const handleBookingSubmit = (bookingData) => {
    console.log('Booking submitted:', bookingData);
    // Handle booking submission
  };

  if (!selectedService) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-text-primary mb-2">Service Not Found</h1>
            <p className="text-text-secondary">The requested service could not be found.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <div className="pt-16">
        <ServiceHero service={selectedService} onBookNow={handleBookNow} />
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Service Process */}
            <ServiceProcess steps={processSteps} />
            
            {/* Before & After Gallery */}
            <BeforeAfterGallery projects={beforeAfterProjects} />
            
            {/* Customer Testimonials */}
            <CustomerTestimonials testimonials={testimonials} />
            
            {/* FAQ Section */}
            <ServiceFAQ faqs={faqs} />
            
            {/* Related Services */}
            <RelatedServices services={relatedServices} />
          </div>
          
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <ServiceSidebar
                service={selectedService}
                onBookNow={handleBookNow}
                onGetQuote={handleGetQuote}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Booking Widget */}
      <div className="booking-widget">
        <BookingWidget
          service={selectedService}
          onBookingSubmit={handleBookingSubmit}
        />
      </div>
      
      {/* Global Components */}
      <ShoppingCart />
      <ContactHub />
      
      {/* Mobile Spacing for Sticky Elements */}
      <div className="h-20 lg:hidden" />
    </div>
  );
};

export default ServiceDetails;